// Isomorphic SVG sanitizer + palette extractor.
// Strategy: strip <script>, on* attrs, javascript: URLs via regex (safe on both client and server).
// Discovers unique fill/stroke colors so the inspector can recolor them.

export interface SanitizedSvg {
  inner: string;          // inner SVG content (no outer <svg> tag), ready to nest
  viewBox: string;        // "minX minY w h"
  width: number;
  height: number;
  palette: string[];      // unique colors discovered (lowercased)
}

const SCRIPT_RE = /<script\b[^>]*>[\s\S]*?<\/script\s*>/gi;
const ON_ATTR_RE = /\son[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi;
const JS_URL_RE = /(href|xlink:href)\s*=\s*(["'])\s*javascript:[^"']*\2/gi;
const FOREIGN_OBJECT_RE = /<foreignObject\b[^>]*>[\s\S]*?<\/foreignObject\s*>/gi;

function stripUnsafe(raw: string): string {
  return raw
    .replace(SCRIPT_RE, "")
    .replace(FOREIGN_OBJECT_RE, "")
    .replace(ON_ATTR_RE, "")
    .replace(JS_URL_RE, "");
}

function parseSvgRoot(raw: string): { attrs: Record<string, string>; inner: string } | null {
  const m = raw.match(/<svg\b([^>]*)>([\s\S]*)<\/svg\s*>/i);
  if (!m) return null;
  const attrStr = m[1];
  const inner = m[2];
  const attrs: Record<string, string> = {};
  const attrRe = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*("([^"]*)"|'([^']*)')/g;
  let am: RegExpExecArray | null;
  while ((am = attrRe.exec(attrStr))) {
    attrs[am[1].toLowerCase()] = am[3] ?? am[4] ?? "";
  }
  return { attrs, inner };
}

function parseLength(v: string | undefined): number | null {
  if (!v) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function normalizeColor(c: string): string | null {
  const v = c.trim().toLowerCase();
  if (!v || v === "none" || v === "transparent" || v === "currentcolor" || v.startsWith("url(")) return null;
  return v;
}

function extractPalette(inner: string): string[] {
  const seen = new Set<string>();
  // attribute form: fill="..." / stroke="..."
  const attrRe = /(fill|stroke)\s*=\s*"([^"]+)"/gi;
  let m: RegExpExecArray | null;
  while ((m = attrRe.exec(inner))) {
    const c = normalizeColor(m[2]);
    if (c) seen.add(c);
  }
  // inline style: fill: ...; stroke: ...;
  const styleRe = /style\s*=\s*"([^"]+)"/gi;
  while ((m = styleRe.exec(inner))) {
    const decls = m[1].split(";");
    for (const d of decls) {
      const [k, v] = d.split(":").map((s) => s && s.trim());
      if ((k === "fill" || k === "stroke") && v) {
        const c = normalizeColor(v);
        if (c) seen.add(c);
      }
    }
  }
  return Array.from(seen).slice(0, 12);
}

/**
 * Sanitize raw SVG markup and extract metadata.
 * Returns `null` for invalid SVGs.
 */
export function sanitizeSvg(raw: string): SanitizedSvg | null {
  const stripped = stripUnsafe(raw);
  const root = parseSvgRoot(stripped);
  if (!root) return null;
  const { attrs, inner } = root;

  const vbAttr = attrs["viewbox"];
  const w = parseLength(attrs.width) ?? 200;
  const h = parseLength(attrs.height) ?? 200;
  let viewBox = vbAttr && vbAttr.trim() ? vbAttr.trim() : `0 0 ${w} ${h}`;

  // derive size from viewBox if width/height missing
  let width = w;
  let height = h;
  const vbParts = viewBox.split(/[\s,]+/).map(Number);
  if (vbParts.length === 4 && vbParts.every((n) => Number.isFinite(n))) {
    if (!attrs.width) width = vbParts[2];
    if (!attrs.height) height = vbParts[3];
  }

  const palette = extractPalette(inner);
  return { inner, viewBox, width, height, palette };
}

/**
 * Apply a palette override map to sanitized inner markup.
 * Replaces fill/stroke attribute values and inline style fill/stroke declarations
 * for any color present in `overrides`.
 */
export function applyPalette(inner: string, overrides: Record<string, string>): string {
  const entries = Object.entries(overrides).filter(([, v]) => !!v);
  if (!entries.length) return inner;
  const map = new Map<string, string>();
  for (const [k, v] of entries) map.set(k.toLowerCase(), v);

  // attribute form
  let out = inner.replace(/(fill|stroke)\s*=\s*"([^"]+)"/gi, (_, attr: string, val: string) => {
    const lo = val.trim().toLowerCase();
    if (map.has(lo)) return `${attr}="${map.get(lo)}"`;
    return `${attr}="${val}"`;
  });

  // inline style form
  out = out.replace(/style\s*=\s*"([^"]+)"/gi, (_, decls: string) => {
    const next = decls
      .split(";")
      .map((d) => {
        const idx = d.indexOf(":");
        if (idx < 0) return d;
        const k = d.slice(0, idx).trim();
        const v = d.slice(idx + 1).trim();
        if (k !== "fill" && k !== "stroke") return d;
        const lo = v.toLowerCase();
        if (map.has(lo)) return ` ${k}: ${map.get(lo)}`;
        return d;
      })
      .join(";");
    return `style="${next}"`;
  });

  return out;
}

/** Stable djb2-style hash for dedupe keys. */
export function hashMarkup(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}
